import React, { useState, useEffect, useRef } from 'react';
import {
  Package,
  MapPin,
  Scan,
  Plus,
  PlusSquare,
  CheckCircle,
  AlertCircle,
  Loader,
  Save,
  RotateCcw,
  Edit2,
  Trash2,
  X,
  Check,
  Search,
  Minus,
  PlusCircle,
  Target,
  Hash
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { MediaCaptureButton } from '../../../common/media/MediaCaptureButton';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { 
  inventoryCorrectionService,
  InventoryCorrectionSession,
  InventoryCorrectionLine,
  InventoryCorrectionFormData,
  InventoryCorrectionRequest,
  ItemLocationInfo,
  LocationItemInfo,
  ReasonCode,
  CompleteCorrectionError
} from '../../../../services/inventoryCorrectionService';
import { warehouseService } from '../../../../services/warehouseService';
import { itemsService } from '../../../../services/itemsService';
import { WarehouseOption } from '../../../../types/warehouse';
import { LocationSelectionModal, ItemSelectionModal } from './modals';
import { SerialCorrectionModal } from './modals/SerialCorrectionModal';
import { BatchCorrectionModal, BatchCorrectionResult } from './modals/BatchCorrectionModal';
import { ItemSearchModal, LocationInfo } from '../../../common/ItemSearchModal';
import { LocationSearchModal, LocationSearchResult } from '../../../common/LocationSearchModal';
import { NumericInput, ScanTextInput } from '../../../common/inputs';
import { ItemsDto } from '../../../../types/items';
import { ExtendShelfModal } from '../../shared/ExtendShelfModal';

interface InventoryCorrectionAppProps {
  onBack: () => void;
}

interface EditingLine {
  lineID: number;
  quantity: string;
}

/**
 * Maks lengde på kommentaren. StcTransaction.ReasonDescription er 255 tegn og får «{årsak} – »
 * foran, så 200 gir alltid plass til årsaksteksten.
 */
const CORRECTION_COMMENT_MAX_LENGTH = 200;

export const InventoryCorrectionApp: React.FC<InventoryCorrectionAppProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouse();
  const [session, setSession] = useState<InventoryCorrectionSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [formData, setFormData] = useState<InventoryCorrectionFormData>({
    scannedCode: '',
    quantity: '',
    transType: 2, // Default: Inn på lager
    reasonCode: 1, // Default: Telleavvik
    internalOnly: false, // Default: send til ERP
    isScanning: false,
    validationMessage: '',
    validationType: ''
  });
  
  // State for correction selections
  const [currentItem, setCurrentItem] = useState<{ itemNo: string; description: string; itemType?: number; itemUnitTxt?: string; } | null>(null);
  const [currentLocation, setCurrentLocation] = useState<{ locationName: string; warehouseID?: string; } | null>(null);
  // Display-only: varenr -> ItemUnitTxt for korreksjonslista. Slås opp mot Items når lista lastes,
  // lagres ALDRI på korreksjonsraden (StcTrans). fetchedUnitsRef hindrer gjentatte oppslag.
  const [itemUnitMap, setItemUnitMap] = useState<Record<string, string>>({});
  // Varebeskrivelse per vare for linjelista — StcTrans.Description1 kan nå være en kommentar (TW 36453035)
  const [itemDescriptionMap, setItemDescriptionMap] = useState<Record<string, string>>({});
  // Valgfri kommentar til neste registrerte korreksjon — tømmes etter hver registrering
  const [comment, setComment] = useState('');
  const fetchedUnitsRef = useRef<Set<string>>(new Set());

  // State for serial correction modal
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [warehouse, setWarehouse] = useState<string>(''); // Will be set when warehouses load
  
  // State for warehouse options from API
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>([]);
  const [warehousesLoading, setWarehousesLoading] = useState(true);
  
  // State for reason codes
  const [reasonCodes, setReasonCodes] = useState<ReasonCode[]>([]);
  
  const [isAdding, setIsAdding] = useState(false);
  const [editingLine, setEditingLine] = useState<EditingLine | null>(null);
  const [deletingLineId, setDeletingLineId] = useState<number | null>(null);
  const [updatingLineId, setUpdatingLineId] = useState<number | null>(null);
  const [filterText, setFilterText] = useState<string>('');
  
  // State for modaler
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showExtendShelfModal, setShowExtendShelfModal] = useState(false);
  const [showItemSearchModal, setShowItemSearchModal] = useState(false);
  const [showLocationSearchModal, setShowLocationSearchModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [availableLocations, setAvailableLocations] = useState<ItemLocationInfo[]>([]);
  const [availableItems, setAvailableItems] = useState<LocationItemInfo[]>([]);
  const [modalLocationName, setModalLocationName] = useState('');
  const [modalWarehouseID, setModalWarehouseID] = useState('');
  
  const scanInputRef = useRef<HTMLInputElement>(null);
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const editQuantityInputRef = useRef<HTMLInputElement>(null);
  const sessionInitialized = useRef(false);

  useEffect(() => {
    if (!sessionInitialized.current) {
      loadCorrectionSession();
      sessionInitialized.current = true;
    }
  }, [user?.operatorId]);
  
  // Load warehouse options and reason codes
  useEffect(() => {
    loadWarehouseOptions();
    loadReasonCodes();
  }, []);

  const loadWarehouseOptions = async () => {
    try {
      console.log('🔗 InventoryCorrectionApp: Starting loadWarehouseOptions()');
      setWarehousesLoading(true);
      console.log('Loading warehouse options...');
      
      const options = await warehouseService.getWarehouseOptions();
      console.log('🔗 InventoryCorrectionApp: Received options:', options);
      setWarehouseOptions(options);
      
      // Set default values when options are first loaded
      if (options.length > 0) {
        console.log('🔗 InventoryCorrectionApp: Setting default warehouse');
        // Set first warehouse as default if not already set
        if (!warehouse) {
          console.log('🔗 Setting warehouse to:', options[0].value);
          setWarehouse(options[0].value);
        }
      }
      
      console.log('Loaded warehouse options:', options);
    } catch (error) {
      console.error('🚨 InventoryCorrectionApp: Error loading warehouse options:', error);
      // Set fallback values if API fails
      if (!warehouse) setWarehouse(currentWarehouse || '1');
    } finally {
      console.log('🔗 InventoryCorrectionApp: Finished loading, setting warehousesLoading to false');
      setWarehousesLoading(false);
    }
  };

  const loadReasonCodes = async () => {
    const codes = await inventoryCorrectionService.fetchReasonCodes();
    setReasonCodes(codes);
    // Sett default reasonCode til første tilgjengelige kode
    if (codes.length > 0) {
      setFormData(prev => ({ ...prev, reasonCode: codes[0].code }));
    }
  };

  useEffect(() => {
    // Auto-focus på scanner input når komponenten lastes
    if (scanInputRef.current && !loading) {
      scanInputRef.current.focus();
    }
  }, [loading]);

  useEffect(() => {
    // Auto-focus på edit input når editing mode starter for en ny linje.
    // Bruker lineID som dependency så effekten ikke re-selekterer teksten
    // på hvert tastetrykk (quantity-endringer skaper nytt editingLine-objekt).
    if (editingLine && editQuantityInputRef.current) {
      editQuantityInputRef.current.focus();
      editQuantityInputRef.current.select();
    }
  }, [editingLine?.lineID]);

  // 🎯 NEW: Auto-focus til quantity-felt når både vare og lokasjon er valgt
  useEffect(() => {
    if (currentItem && currentLocation && quantityInputRef.current) {
      // Vent litt for å sikre at UI er oppdatert før fokus settes
      setTimeout(() => {
        quantityInputRef.current?.focus();
      }, 100);
    }
  }, [currentItem, currentLocation]);

  // TW 36453035: StcTrans.Description1 er kommentaren når operatøren skrev en, ellers varebeskrivelsen.
  // Varenavnet hentes derfor fra varen; Description1 vises som kommentar bare når den avviker.
  const lineItemDescription = (line: InventoryCorrectionLine): string =>
    itemDescriptionMap[line.itemNo] || line.description1 || '';
  const lineComment = (line: InventoryCorrectionLine): string => {
    const text = (line.description1 || '').trim();
    const itemDescription = itemDescriptionMap[line.itemNo];
    if (!text || !itemDescription) return '';
    return text.toLowerCase() === itemDescription.trim().toLowerCase() ? '' : text;
  };

  // Filter korreksjoner basert på varenummer og lokasjonskode
  const filteredLines = session?.lines.filter((line: InventoryCorrectionLine) => {
    if (!filterText.trim()) return true;

    const searchTerm = filterText.toLowerCase();
    const itemNo = line.itemNo.toLowerCase();
    const location = line.locationName.toLowerCase();
    const description = `${lineItemDescription(line)} ${line.description1 || ''}`.toLowerCase();
    const serialNo = (line.itemSerialNo || '').toLowerCase();
    const batchNo = (line.itemBatchNo || '').toLowerCase();

    return itemNo.includes(searchTerm) ||
           location.includes(searchTerm) ||
           description.includes(searchTerm) ||
           serialNo.includes(searchTerm) ||
           batchNo.includes(searchTerm);
  }) || [];

  // Display-only: hent ItemUnitTxt fra Items for varenr i lista som ikke alt er slått opp.
  // Rent visning — enheten lagres aldri på korreksjonsraden.
  useEffect(() => {
    const lines = session?.lines;
    const companyNo = user?.companyNo;
    if (!lines || lines.length === 0 || !companyNo) return;

    const missing = Array.from(new Set(lines.map(l => l.itemNo).filter(Boolean)))
      .filter(itemNo => !fetchedUnitsRef.current.has(itemNo));
    if (missing.length === 0) return;

    missing.forEach(itemNo => fetchedUnitsRef.current.add(itemNo));
    let active = true;
    (async () => {
      const entries = await Promise.all(missing.map(async (itemNo) => {
        try {
          const item = await itemsService.getItemByCompanyAndItemNo(companyNo, itemNo);
          return [itemNo, item?.itemUnitTxt || '', item?.description1 || ''] as const;
        } catch {
          return [itemNo, '', ''] as const;
        }
      }));
      if (!active) return;
      setItemUnitMap(prev => {
        const next = { ...prev };
        for (const [k, v] of entries) next[k] = v;
        return next;
      });
      setItemDescriptionMap(prev => {
        const next = { ...prev };
        for (const [k, , d] of entries) if (d) next[k] = d;
        return next;
      });
    })();
    return () => { active = false; };
  }, [session?.lines, user?.companyNo]);

  const loadCorrectionSession = async () => {
    if (!user?.operatorId || sessionLoading) return;

    setSessionLoading(true);
    setLoading(true);
    
    try {
      console.log('Loading correction session for operator:', user.operatorId);
      
      // Søk etter eksisterende aktiv session
      const activeSession = await inventoryCorrectionService.getActiveCorrectionSession(user.operatorId);
      
      if (activeSession) {
        console.log('Found existing active correction session:', activeSession.batchID);
        setSession(activeSession);
      } else {
        console.log('No active correction session found, creating new one');
        // Opprett ny session første gang
        const newSession = await inventoryCorrectionService.createCorrectionSession(user.operatorId, warehouse);
        console.log('Created new correction session:', newSession.batchID);
        setSession(newSession);
      }
    } catch (error) {
      console.error('Feil ved lasting av korreksjonssession:', error);
      setFormData((prev: InventoryCorrectionFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventoryCorrection.errors.loadSessionFailed'),
        validationType: 'error'
      }));
    } finally {
      setSessionLoading(false);
      setLoading(false);
    }
  };

  const handleScanInput = async (value: string) => {
    if (!value.trim()) return;

    setFormData((prev: InventoryCorrectionFormData) => ({ ...prev, isScanning: true, validationMessage: '' }));

    try {
      // Prøv først som vare
      const itemValidation = await inventoryCorrectionService.validateItem(value);
      if (itemValidation.valid) {
        // Sjekk om varen finnes på flere lokasjoner
        const locations = await inventoryCorrectionService.getLocationsForItem(itemValidation.itemNo, warehouse || currentWarehouse); // TW 36386708: kun aktivt lager
        
        if (locations.length === 0) {
          // Inn på lager (transType 2): varen trenger ikke eksisterende saldo.
          // Sett varen og be operatøren skanne/velge destinasjonslokasjon.
          if (formData.transType === 2) {
            setCurrentItem({
              itemNo: itemValidation.itemNo,
              description: itemValidation.description1 || '',
              itemType: itemValidation.itemType,
              itemUnitTxt: itemValidation.itemUnitTxt
            });
            setCurrentLocation(null);
            setFormData((prev: InventoryCorrectionFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.inventoryCorrection.messages.itemSetScanLocation', { '0': itemValidation.itemNo }),
              validationType: 'success'
            }));
            setTimeout(() => scanInputRef.current?.focus(), 100);
            return;
          }

          // Ut av lager (transType 1): kan ikke trekke ut en vare som ikke finnes på lager.
          setFormData((prev: InventoryCorrectionFormData) => ({
            ...prev,
            isScanning: false,
            validationMessage: t('warehouse.modules.inventoryCorrection.errors.itemNotFoundOnAnyLocations', { '0': itemValidation.itemNo }),
            validationType: 'error'
          }));
          setTimeout(() => scanInputRef.current?.focus(), 100);
          return;
        } else if (locations.length === 1) {
          // Kun en lokasjon - sett automatisk
          setCurrentItem({
            itemNo: itemValidation.itemNo,
            description: itemValidation.description1 || '',
            itemType: itemValidation.itemType,
            itemUnitTxt: itemValidation.itemUnitTxt
          });
          setCurrentLocation({
            locationName: locations[0].locationName,
            warehouseID: locations[0].warehouseID
          });
          setFormData((prev: InventoryCorrectionFormData) => ({
            ...prev,
            isScanning: false,
            validationMessage: t('warehouse.modules.inventoryCorrection.messages.itemAndLocationSet', { '0': itemValidation.itemNo, '1': locations[0].locationName }),
            validationType: 'success'
          }));
          setTimeout(() => scanInputRef.current?.focus(), 100);
          return;
        } else {
          // Flere lokasjoner - vis modal
          setCurrentItem({
            itemNo: itemValidation.itemNo,
            description: itemValidation.description1 || '',
            itemType: itemValidation.itemType,
            itemUnitTxt: itemValidation.itemUnitTxt
          });
          setAvailableLocations(locations);
          setShowLocationModal(true);
          setFormData((prev: InventoryCorrectionFormData) => ({
            ...prev,
            isScanning: false,
            validationMessage: '',
            validationType: ''
          }));
          return;
        }
      }

      // Prøv som lokasjon
      const locationValidation = await inventoryCorrectionService.validateLocation(value);
      if (locationValidation.valid) {
        // Hvis vi ikke har en vare valgt ennå, sjekk hvilke varer som finnes på lokasjonen
        if (!currentItem) {
          const items = await inventoryCorrectionService.getItemsAtLocation(locationValidation.locationName);
          
          if (items.length === 0) {
            setFormData((prev: InventoryCorrectionFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.inventoryCorrection.errors.locationHasNoItems', { '0': locationValidation.locationName }),
              validationType: 'error'
            }));
            setTimeout(() => scanInputRef.current?.focus(), 100);
            return;
          } else if (items.length === 1) {
            // Kun en vare - sett automatisk
            setCurrentItem({
              itemNo: items[0].itemNo,
              description: items[0].description,
              itemType: items[0].itemType
            });
            setCurrentLocation({
              locationName: locationValidation.locationName,
              warehouseID: locationValidation.warehouseID
            });
            setFormData((prev: InventoryCorrectionFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.inventoryCorrection.messages.itemAndLocationSet', { '0': items[0].itemNo, '1': locationValidation.locationName }),
              validationType: 'success'
            }));
            setTimeout(() => scanInputRef.current?.focus(), 100);
            return;
          } else {
            // Flere varer - vis modal
            setAvailableItems(items);
            setModalLocationName(locationValidation.locationName);
            setModalWarehouseID(locationValidation.warehouseID || '');
            setShowItemModal(true);
            setFormData((prev: InventoryCorrectionFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: '',
              validationType: ''
            }));
            return;
          }
        } else {
          // Vi har allerede en vare, så sett lokasjonen
          setCurrentLocation({
            locationName: locationValidation.locationName,
            warehouseID: locationValidation.warehouseID
          });
          setFormData((prev: InventoryCorrectionFormData) => ({
            ...prev,
            isScanning: false,
            validationMessage: t('warehouse.modules.inventoryCorrection.messages.locationSet', { '0': locationValidation.locationName }),
            validationType: 'success'
          }));

          // 🎯 Fokus vil automatisk settes til quantity-feltet via useEffect
          if (!currentItem || !currentLocation) {
            setTimeout(() => scanInputRef.current?.focus(), 100);
          }
          return;
        }
      }

      // Ingen match funnet
      setFormData((prev: InventoryCorrectionFormData) => ({
        ...prev,
        isScanning: false,
        validationMessage: t('warehouse.modules.inventoryCorrection.errors.notFoundAsItemOrLocation', { '0': value }),
        validationType: 'error'
      }));
      setTimeout(() => scanInputRef.current?.focus(), 100);

    } catch (error) {
      console.error('Feil ved validering:', error);
      setFormData((prev: InventoryCorrectionFormData) => ({
        ...prev,
        isScanning: false,
        validationMessage: t('warehouse.modules.inventoryCorrection.errors.validationError'),
        validationType: 'error'
      }));
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  };

  const handleLocationSelection = (location: ItemLocationInfo) => {
    setCurrentLocation({
      locationName: location.locationName,
      warehouseID: location.warehouseID
    });
    setFormData((prev: InventoryCorrectionFormData) => ({
      ...prev,
      validationMessage: t('warehouse.modules.inventoryCorrection.messages.locationSelected', { '0': location.locationName, '1': location.totalQuantity }),
      validationType: 'success'
    }));
    // Fokus på scanner eller quantity-felt
    setTimeout(() => {
      if (currentItem && currentLocation) {
        quantityInputRef.current?.focus();
      } else {
        scanInputRef.current?.focus();
      }
    }, 100);
  };

  // Skann en lokasjon i lokasjonsmodalen for å bestemme hvilken lokasjon varen korrigeres på.
  // Matcher først mot listen varen allerede ligger på; ellers valideres lokasjonen og velges
  // som ny korreksjonslokasjon (saldo 0). Returnerer oppløst lokasjon, eller null hvis ugyldig.
  const handleScanLocationInModal = async (code: string): Promise<ItemLocationInfo | null> => {
    const trimmed = code.trim();
    if (!trimmed || !currentItem) return null;

    const match = availableLocations.find(
      l => l.locationName.toLowerCase() === trimmed.toLowerCase()
    );
    if (match) return match;

    const validation = await inventoryCorrectionService.validateLocation(trimmed);
    if (!validation.valid) return null;

    return {
      itemNo: currentItem.itemNo,
      description: currentItem.description,
      locationName: validation.locationName,
      warehouseID: validation.warehouseID || '',
      availableQuantity: 0,
      totalQuantity: 0,
      reservedQuantity: 0,
      binCodeFixed: false,
      binCodePrimary: false
    };
  };

  const handleItemSelection = (item: LocationItemInfo) => {
    setCurrentItem({
      itemNo: item.itemNo,
      description: item.description,
      itemType: item.itemType
    });
    setCurrentLocation({
      locationName: item.locationName,
      warehouseID: item.warehouseID
    });
    setFormData((prev: InventoryCorrectionFormData) => ({
      ...prev,
      validationMessage: t('warehouse.modules.inventoryCorrection.messages.itemAndLocationSetWithQuantity', { '0': item.itemNo, '1': item.locationName, '2': item.totalQuantity }),
      validationType: 'success'
    }));
    // Fokus vil automatisk gå til quantity-felt via useEffect
    setTimeout(() => quantityInputRef.current?.focus(), 100);
  };

  const handleItemSearchSelect = async (item: ItemsDto, location: LocationInfo) => {
    setShowItemSearchModal(false);

    // Set item and location from search modal
    setCurrentItem({
      itemNo: item.itemNo,
      description: item.description1 || '',
      itemType: item.itemType,
      itemUnitTxt: item.itemUnitTxt
    });
    setCurrentLocation({
      locationName: location.locationName,
      warehouseID: location.warehouseID
    });
    setFormData((prev: InventoryCorrectionFormData) => ({
      ...prev,
      validationMessage: t('warehouse.modules.inventoryCorrection.messages.itemAndLocationSetWithQuantity', { '0': item.itemNo, '1': location.locationName, '2': location.availableQuantity }),
      validationType: 'success'
    }));

    // Focus will automatically go to quantity field via useEffect
    setTimeout(() => quantityInputRef.current?.focus(), 100);
  };

  const handleLocationSearchSelect = (location: LocationSearchResult) => {
    setShowLocationSearchModal(false);

    // Set location
    setCurrentLocation({
      locationName: location.binCode,
      warehouseID: location.warehouseID
    });
    setFormData((prev: InventoryCorrectionFormData) => ({
      ...prev,
      validationMessage: t('warehouse.modules.inventoryCorrection.messages.locationSet', { '0': location.binCode }),
      validationType: 'success'
    }));

    // Focus will automatically go to quantity field via useEffect (since item is already set)
    setTimeout(() => quantityInputRef.current?.focus(), 100);
  };

  const handleAddCorrection = async () => {
    if (!session || !currentItem || !currentLocation) {
      setFormData((prev: InventoryCorrectionFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventoryCorrection.errors.mustScanItemAndLocation'),
        validationType: 'error'
      }));
      return;
    }

    const quantity = parseFloat(formData.quantity);
    if (isNaN(quantity) || quantity === 0) {
      setFormData((prev: InventoryCorrectionFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventoryCorrection.errors.mustEnterValidQuantity'),
        validationType: 'error'
      }));
      setTimeout(() => quantityInputRef.current?.focus(), 100);
      return;
    }

    setIsAdding(true);
    try {
      const correctionData: InventoryCorrectionRequest = {
        itemNo: currentItem.itemNo,
        locationName: currentLocation.locationName,
        quantity,
        warehouse,
        transType: formData.transType,
        reasonCode: formData.reasonCode,
        description1: comment.trim() || currentItem.description,
        internalOnly: formData.internalOnly
      };

      const newLine = await inventoryCorrectionService.addCorrectionLine(session.batchID, correctionData);
      
      // Oppdater session med ny linje
      setSession((prev: InventoryCorrectionSession | null) => prev ? {
        ...prev,
        lines: [newLine, ...prev.lines],
        transactionCount: prev.transactionCount + 1
      } : null);

      // Reset form for neste korreksjon
      setFormData({
        scannedCode: '',
        quantity: '',
        transType: formData.transType, // Behold transtype
        reasonCode: formData.reasonCode, // Behold årsakskode
        internalOnly: formData.internalOnly, // Behold intern-flagg
        isScanning: false,
        validationMessage: t('warehouse.modules.inventoryCorrection.messages.correctionAdded', { '0': currentItem.itemNo, '1': quantity > 0 ? '+' : '', '2': quantity, '3': formData.transType === 1 ? t('warehouse.modules.inventoryCorrection.labels.outOfStock') : t('warehouse.modules.inventoryCorrection.labels.intoStock'), '4': currentLocation.locationName }),
        validationType: 'success'
      });
      setCurrentItem(null);
      setCurrentLocation(null);
      setComment('');

      // 🎯 Auto-focus tilbake til scanner etter vellykket korreksjon
      setTimeout(() => scanInputRef.current?.focus(), 100);

    } catch (error) {
      console.error('Feil ved adding av korreksjonslinje:', error);
      setFormData((prev: InventoryCorrectionFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventoryCorrection.errors.couldNotAddCorrection'),
        validationType: 'error'
      }));
    } finally {
      setIsAdding(false);
    }
  };

  // Handle serial correction completion — adds one line per serial number
  const handleSerialCorrectionComplete = async (serialNumbers: string[]) => {
    if (!session || !currentItem || !currentLocation) return;
    setShowSerialModal(false);
    setIsAdding(true);

    try {
      const newLines: InventoryCorrectionLine[] = [];
      for (const serialNo of serialNumbers) {
        const correctionData: InventoryCorrectionRequest = {
          itemNo: currentItem.itemNo,
          locationName: currentLocation.locationName,
          quantity: 1,
          warehouse,
          transType: formData.transType,
          reasonCode: formData.reasonCode,
          description1: comment.trim() || currentItem.description,
          itemSerialNo: serialNo,
          itemType: currentItem.itemType,
          internalOnly: formData.internalOnly
        };
        const newLine = await inventoryCorrectionService.addCorrectionLine(session.batchID, correctionData);
        newLines.push(newLine);
      }

      // Update session with all new lines
      setSession((prev: InventoryCorrectionSession | null) => prev ? {
        ...prev,
        lines: [...newLines.reverse(), ...prev.lines],
        transactionCount: prev.transactionCount + newLines.length
      } : null);

      // Reset form
      setFormData({
        scannedCode: '',
        quantity: '',
        transType: formData.transType,
        reasonCode: formData.reasonCode,
        internalOnly: formData.internalOnly,
        isScanning: false,
        validationMessage: formData.transType === 1
          ? t('warehouse.modules.inventoryCorrection.serialBatch.serialsRemoved', { '0': serialNumbers.length, '1': currentItem.itemNo }, `${serialNumbers.length} serienummer tatt ut for ${currentItem.itemNo}`)
          : t('warehouse.modules.inventoryCorrection.serialBatch.serialsAdded', { '0': serialNumbers.length, '1': currentItem.itemNo }, `${serialNumbers.length} serienummer lagt inn for ${currentItem.itemNo}`),
        validationType: 'success'
      });
      setCurrentItem(null);
      setCurrentLocation(null);
      setComment('');
      setTimeout(() => scanInputRef.current?.focus(), 100);

    } catch (error) {
      console.error('Feil ved serial-korreksjon:', error);
      setFormData((prev: InventoryCorrectionFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventoryCorrection.errors.couldNotAddSerialCorrection', {}, 'Kunne ikke legge til serienummer-korreksjon'),
        validationType: 'error'
      }));
    } finally {
      setIsAdding(false);
    }
  };

  // Handle batch correction completion — adds one line per batch with variable qty
  const handleBatchCorrectionComplete = async (batches: BatchCorrectionResult[]) => {
    if (!session || !currentItem || !currentLocation) return;
    setShowBatchModal(false);
    setIsAdding(true);

    try {
      const newLines: InventoryCorrectionLine[] = [];
      for (const batch of batches) {
        const correctionData: InventoryCorrectionRequest = {
          itemNo: currentItem.itemNo,
          locationName: currentLocation.locationName,
          quantity: batch.quantity,
          warehouse,
          transType: formData.transType,
          reasonCode: formData.reasonCode,
          description1: comment.trim() || currentItem.description,
          itemBatchNo: batch.batchNo,
          itemBatchExpDate: batch.expiryDate ? new Date(batch.expiryDate) : undefined,
          itemType: currentItem.itemType,
          internalOnly: formData.internalOnly
        };
        const newLine = await inventoryCorrectionService.addCorrectionLine(session.batchID, correctionData);
        newLines.push(newLine);
      }

      // Update session with all new lines
      setSession((prev: InventoryCorrectionSession | null) => prev ? {
        ...prev,
        lines: [...newLines.reverse(), ...prev.lines],
        transactionCount: prev.transactionCount + newLines.length
      } : null);

      // Reset form
      setFormData({
        scannedCode: '',
        quantity: '',
        transType: formData.transType,
        reasonCode: formData.reasonCode,
        internalOnly: formData.internalOnly,
        isScanning: false,
        validationMessage: formData.transType === 1
          ? t('warehouse.modules.inventoryCorrection.serialBatch.batchesRemoved', { '0': batches.length, '1': currentItem.itemNo }, `${batches.length} batcher tatt ut for ${currentItem.itemNo}`)
          : t('warehouse.modules.inventoryCorrection.serialBatch.batchesAdded', { '0': batches.length, '1': currentItem.itemNo }, `${batches.length} batcher lagt inn for ${currentItem.itemNo}`),
        validationType: 'success'
      });
      setCurrentItem(null);
      setCurrentLocation(null);
      setComment('');
      setTimeout(() => scanInputRef.current?.focus(), 100);

    } catch (error) {
      console.error('Feil ved batch-korreksjon:', error);
      setFormData((prev: InventoryCorrectionFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventoryCorrection.errors.couldNotAddBatchCorrection', {}, 'Kunne ikke legge til batch-korreksjon'),
        validationType: 'error'
      }));
    } finally {
      setIsAdding(false);
    }
  };

  const handleStartEdit = (line: InventoryCorrectionLine) => {
    setEditingLine({
      lineID: line.lineID,
      quantity: line.quantity.toString()
    });
  };

  const handleCancelEdit = () => {
    setEditingLine(null);
  };

  const handleSaveEdit = async () => {
    if (!editingLine || !session) return;

    const quantity = parseFloat(editingLine.quantity);
    if (isNaN(quantity) || quantity === 0) {
      setFormData((prev: InventoryCorrectionFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventoryCorrection.errors.mustEnterValidQuantity'),
        validationType: 'error'
      }));
      return;
    }

    setUpdatingLineId(editingLine.lineID);
    try {
      const updatedLine = await inventoryCorrectionService.updateCorrectionLine(session.batchID, editingLine.lineID, quantity);
      
      // Oppdater session med oppdatert linje
      setSession((prev: InventoryCorrectionSession | null) => prev ? {
        ...prev,
        lines: prev.lines.map(line => 
          line.lineID === editingLine.lineID ? updatedLine : line
        )
      } : null);

      setEditingLine(null);
      setFormData((prev: InventoryCorrectionFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventoryCorrection.messages.quantityUpdated', { '0': quantity }),
        validationType: 'success'
      }));

      // Clear message after 3 seconds
      setTimeout(() => {
        setFormData((prev: InventoryCorrectionFormData) => ({
          ...prev,
          validationMessage: '',
          validationType: ''
        }));
      }, 3000);

    } catch (error) {
      console.error('Feil ved oppdatering av korreksjonslinje:', error);
      setFormData((prev: InventoryCorrectionFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventoryCorrection.errors.couldNotUpdateCorrection'),
        validationType: 'error'
      }));
    } finally {
      setUpdatingLineId(null);
    }
  };

  const handleDeleteLine = async (line: InventoryCorrectionLine) => {
    if (!session) return;

    const confirmDelete = window.confirm(
      t('warehouse.modules.inventoryCorrection.confirmations.deleteCorrection', { '0': line.itemNo, '1': lineItemDescription(line), '2': line.locationName, '3': line.quantity, '4': line.transTypeText })
    );
    if (!confirmDelete) return;

    setDeletingLineId(line.lineID);
    try {
      const success = await inventoryCorrectionService.deleteCorrectionLine(session.batchID, line.lineID);
      
      if (success) {
        // Fjern linje fra session
        setSession((prev: InventoryCorrectionSession | null) => prev ? {
          ...prev,
          lines: prev.lines.filter(l => l.lineID !== line.lineID),
          transactionCount: prev.transactionCount - 1
        } : null);

        setFormData((prev: InventoryCorrectionFormData) => ({
          ...prev,
          validationMessage: t('warehouse.modules.inventoryCorrection.messages.correctionDeleted', { '0': line.itemNo }),
          validationType: 'success'
        }));

        setTimeout(() => {
          setFormData((prev: InventoryCorrectionFormData) => ({
            ...prev,
            validationMessage: '',
            validationType: ''
          }));
        }, 3000);
      }

    } catch (error) {
      console.error('Feil ved sletting av korreksjonslinje:', error);
      setFormData((prev: InventoryCorrectionFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventoryCorrection.errors.couldNotDeleteCorrection'),
        validationType: 'error'
      }));
    } finally {
      setDeletingLineId(null);
    }
  };

  const handleReset = () => {
    setFormData({
      scannedCode: '',
      quantity: '',
      transType: formData.transType, // Behold transtype
      reasonCode: formData.reasonCode, // Behold årsakskode
      internalOnly: formData.internalOnly, // Behold intern-flagg
      isScanning: false,
      validationMessage: '',
      validationType: ''
    });
    setCurrentItem(null);
    setCurrentLocation(null);
    setComment('');
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const handleCompleteSession = async () => {
    if (!session) return;

    if (session.lines.length === 0) {
      setFormData((prev: InventoryCorrectionFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventoryCorrection.errors.cannotCompleteEmptySession'),
        validationType: 'error'
      }));
      return;
    }

    const confirmComplete = window.confirm(t('warehouse.modules.inventoryCorrection.confirmations.completeSession', { '0': session.lines.length }));
    if (!confirmComplete) return;

    try {
      await inventoryCorrectionService.completeCorrectionSession(session.batchID);
      alert(t('warehouse.modules.inventoryCorrection.messages.sessionCompleted'));
      onBack();
    } catch (error) {
      console.error('Feil ved fullføring:', error);

      // Serverens melding peker normalt på hvilken vare/linje som blokkerer
      // fullføringen (f.eks. utilstrekkelig saldo). Vis den ordrett i stedet for
      // en generisk tekst, og filtrer linjelista på den aktuelle varen slik at
      // operatøren raskt finner linjen og kan rette antallet eller slette den.
      const completeError = error as CompleteCorrectionError;
      const serverMessage = completeError?.message?.trim();
      const offendingItemNo = completeError?.details?.itemNo?.trim();

      let message = serverMessage && serverMessage.length > 0
        ? serverMessage
        : t('warehouse.modules.inventoryCorrection.errors.couldNotCompleteSession');

      if (offendingItemNo) {
        setFilterText(offendingItemNo);
        message = `${message} ${t('warehouse.modules.inventoryCorrection.errors.completeFailedLineHint', {}, 'Listen under er filtrert på varen det gjelder – rett antallet eller slett linjen og prøv igjen.')}`;
      }

      setFormData((prev: InventoryCorrectionFormData) => ({
        ...prev,
        validationMessage: message,
        validationType: 'error'
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin h-8 w-8 text-red-500" />
        <span className="ml-2">{t('warehouse.modules.inventoryCorrection.messages.loadingSession')}</span>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-4 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-lg font-semibold mb-2">{t('warehouse.modules.inventoryCorrection.errors.couldNotLoadSession')}</h2>
        <button
          onClick={onBack}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          {t('common.buttons.backToDashboard')}
        </button>
      </div>
    );
  }

  const reasonCodeDescription = inventoryCorrectionService.getReasonCodeDescription(formData.reasonCode);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-3 sm:px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
            <BackButton onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0" iconSize={20} label="" />
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{t('warehouse.modules.inventoryCorrection.title')}</h1>
              <p className="text-xs sm:text-sm text-gray-500 truncate">{session.description1}</p>
            </div>
            <MediaCaptureButton
              entityType="InventoryCorrection"
              entityKey={session.batchID}
            />
            <button
              onClick={() => setShowExtendShelfModal(true)}
              className="p-2 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors"
              title={t('warehouse.extendShelf.buttonTitle')}
              aria-label={t('warehouse.extendShelf.buttonTitle')}
            >
              <PlusSquare className="h-5 w-5" />
            </button>
          </div>
          <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
            <span className="text-xs sm:text-sm bg-red-100 text-red-800 px-2 py-1 rounded whitespace-nowrap">
              {session.lines.length}
            </span>
            {session.lines.length > 0 && (
              <button
                onClick={handleCompleteSession}
                className="bg-green-500 text-white px-2 sm:px-3 py-1 rounded text-xs sm:text-sm hover:bg-green-600 flex items-center space-x-1 min-h-[44px] sm:min-h-0"
              >
                <Save className="h-4 w-4" />
                <span className="hidden sm:inline">{t('common.buttons.complete')}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4 max-w-4xl mx-auto space-y-3 sm:space-y-4">
        {/* Korreksjon-innstillinger */}
        <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border">
          <h2 className="text-sm sm:text-base font-semibold mb-3 flex items-center">
            <Target className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-500" />
            {t('warehouse.modules.inventoryCorrection.labels.correctionSettings')}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.general.warehouse')}
              </label>
              <select
                value={warehouse}
                onChange={(e) => setWarehouse(e.target.value)}
                className="w-full p-2 sm:p-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm min-h-[44px] sm:min-h-0"
                disabled={warehousesLoading}
              >
                {warehousesLoading ? (
                  <option>{t('warehouse.modules.inventoryCorrection.messages.loadingWarehouses')}</option>
                ) : warehouseOptions.length > 0 ? (
                  warehouseOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="1">{t('warehouse.modules.inventoryCorrection.labels.mainWarehouse')}</option>
                    <option value="2">{t('warehouse.modules.inventoryCorrection.labels.reserveWarehouse')}</option>
                    <option value="3">{t('warehouse.modules.inventoryCorrection.labels.returnWarehouse')}</option>
                  </>
                )}
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                {t('common.fields.type')}
              </label>
              <select
                value={formData.transType}
                onChange={(e) => setFormData(prev => ({ ...prev, transType: parseInt(e.target.value) }))}
                className="w-full p-2 sm:p-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm min-h-[44px] sm:min-h-0"
              >
                <option value={2}>{t('warehouse.modules.inventoryCorrection.labels.intoStock')}</option>
                <option value={1}>{t('warehouse.modules.inventoryCorrection.labels.outOfStock')}</option>
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.modules.inventoryCorrection.reasonCode')}
              </label>
              <select
                value={formData.reasonCode}
                onChange={(e) => setFormData(prev => ({ ...prev, reasonCode: parseInt(e.target.value) }))}
                className="w-full p-2 sm:p-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm min-h-[44px] sm:min-h-0"
              >
                {reasonCodes.map(reason => (
                  <option key={reason.code} value={reason.code}>
                    {reason.description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Kommentar (TW 36453035): valgfri, gjelder neste registrerte linje(r). Lagres i
              StcTrans.Description1 → ERP-filen felt 5 og «{årsak} – {kommentar}» i historikken.
              Tom kommentar = varebeskrivelsen sendes som før. */}
          <div className="mt-3">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.modules.inventoryCorrection.comment', {}, 'Kommentar')}
              <span className="ml-1 font-normal text-gray-400">{t('warehouse.modules.inventoryCorrection.commentOptional', {}, '(valgfri)')}</span>
            </label>
            <ScanTextInput
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, CORRECTION_COMMENT_MAX_LENGTH))}
              maxLength={CORRECTION_COMMENT_MAX_LENGTH}
              placeholder={t('warehouse.modules.inventoryCorrection.commentPlaceholder', {}, 'F.eks. knust ved mottak')}
              className="w-full p-2 sm:p-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm min-h-[44px] sm:min-h-0"
            />
          </div>

          {/* Intern korreksjon toggle */}
          <div className="mt-3 flex items-center">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.internalOnly}
                onChange={(e) => setFormData(prev => ({ ...prev, internalOnly: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-500"></div>
              <span className="ms-2 text-xs sm:text-sm font-medium text-gray-700">
                {t('warehouse.modules.inventoryCorrection.labels.internalOnly')}
              </span>
            </label>
            {formData.internalOnly && (
              <span className="ml-2 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                {t('warehouse.modules.inventoryCorrection.labels.internalOnlyHint')}
              </span>
            )}
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-lg p-3 shadow-sm border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center space-x-2">
              <Scan className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
              <span className="text-sm sm:text-base font-medium text-gray-700">{t('warehouse.modules.inventoryCorrection.labels.quickSelection')}</span>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <button
                onClick={() => setShowItemSearchModal(true)}
                className="flex items-center justify-center space-x-2 px-4 py-2.5 sm:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm min-h-[44px] sm:min-h-0"
              >
                <Search className="h-4 w-4" />
                <span>{t('warehouse.modules.inventoryCorrection.actions.searchItem')}</span>
              </button>
              <button
                onClick={() => setShowLocationSearchModal(true)}
                disabled={!currentItem}
                className={`flex items-center justify-center space-x-2 px-4 py-2.5 sm:py-2 rounded-lg transition-colors text-sm min-h-[44px] sm:min-h-0 ${
                  currentItem
                    ? 'bg-purple-500 text-white hover:bg-purple-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                title={!currentItem ? t('warehouse.modules.inventoryCorrection.errors.selectItemFirst') : t('warehouse.modules.inventoryCorrection.actions.searchLocation')}
              >
                <MapPin className="h-4 w-4" />
                <span>{t('warehouse.modules.inventoryCorrection.actions.searchLocation')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Scanning og Input sektor */}
        <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border">
          <h2 className="text-sm sm:text-base font-semibold mb-3 flex items-center">
            <Scan className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-500" />
            {t('warehouse.modules.inventoryCorrection.labels.scanItemAndLocation')}
          </h2>

          <div className="space-y-3">
            {/* Scanner input */}
            <div className="relative">
              <input
                ref={scanInputRef}
                type="text"
                enterKeyHint="send"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={formData.scannedCode}
                onChange={(e) => setFormData((prev: InventoryCorrectionFormData) => ({ ...prev, scannedCode: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && formData.scannedCode.trim()) {
                    handleScanInput(formData.scannedCode.trim());
                    setFormData((prev: InventoryCorrectionFormData) => ({ ...prev, scannedCode: '' }));
                  }
                }}
                placeholder={t('warehouse.modules.inventoryCorrection.placeholders.scanOrTypeItemLocation')}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm min-h-[44px]"
                disabled={formData.isScanning || showLocationModal || showItemModal || showItemSearchModal || showLocationSearchModal}
                autoFocus
              />
              {formData.isScanning && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader className="animate-spin h-5 w-5 text-red-500" />
                </div>
              )}
            </div>

            {/* Validation message */}
            {formData.validationMessage && (
              <div className={`p-2.5 sm:p-2 rounded flex items-start sm:items-center text-xs sm:text-sm ${
                formData.validationType === 'success'
                  ? 'bg-green-50 text-green-800'
                  : 'bg-red-50 text-red-800'
              }`}>
                {formData.validationType === 'success' ? (
                  <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5 sm:mt-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5 sm:mt-0" />
                )}
                <span className="break-words">{formData.validationMessage}</span>
              </div>
            )}

            {/* Current selections */}
            <div className="space-y-2 sm:space-y-3">
              {/* Current Item */}
              <div className={`p-3 border rounded-lg ${currentItem ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center">
                  <Package className={`h-4 w-4 mr-2 flex-shrink-0 ${currentItem ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className="text-sm font-medium">{t('warehouse.general.item')}</span>
                </div>
                {currentItem ? (
                  <div className="mt-1.5">
                    <div className="font-mono text-sm break-all">
                      {currentItem.itemNo}
                      {currentItem.itemUnitTxt && (
                        <span className="ml-2 font-sans font-normal text-xs text-gray-400">{currentItem.itemUnitTxt}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 break-words mt-0.5">{currentItem.description}</div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 mt-1">{t('warehouse.modules.inventoryCorrection.labels.notScanned')}</div>
                )}
              </div>

              {/* Current Location */}
              <div className={`p-3 border rounded-lg ${currentLocation ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center">
                  <MapPin className={`h-4 w-4 mr-2 flex-shrink-0 ${currentLocation ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className="text-sm font-medium">{t('warehouse.general.location')}</span>
                </div>
                {currentLocation ? (
                  <div className="mt-1.5">
                    <div className="font-mono text-sm break-all">{currentLocation.locationName}</div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 mt-1">{t('warehouse.modules.inventoryCorrection.labels.notScanned')}</div>
                )}
              </div>
            </div>

            {/* Correction details - vis aktuelle innstillinger */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="text-xs sm:text-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                  <span className="font-medium">{t('warehouse.modules.inventoryCorrection.labels.correctionWillBe')}</span>
                  <div className="flex items-center space-x-2">
                    {formData.transType === 1 ? (
                      <Minus className="h-4 w-4 text-red-500 flex-shrink-0" />
                    ) : (
                      <PlusCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    )}
                    <span className="font-medium">
                      {formData.transType === 1 ? t('warehouse.modules.inventoryCorrection.labels.outOfStock') : t('warehouse.modules.inventoryCorrection.labels.intoStock')}
                    </span>
                  </div>
                </div>
                <div className="text-gray-600">
                  {t('warehouse.modules.inventoryCorrection.labels.reason')}: {reasonCodeDescription}
                </div>
                {formData.internalOnly && (
                  <div className="text-amber-600 font-medium mt-1">
                    {t('warehouse.modules.inventoryCorrection.labels.internalOnlyHint')}
                  </div>
                )}
              </div>
            </div>

            {/* Quantity input / Serial button / Batch button - kun vis når alt er skannet */}
            {currentItem && currentLocation && (
              currentItem.itemType === 6 ? (
                /* Serial item (ItemType=6) → vis serienummer-knapp */
                <div className="space-y-2">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Hash className="h-4 w-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-800">{t('warehouse.modules.inventoryCorrection.serialBatch.serialControlledItem', {}, 'Serienummerstyrt vare')}</span>
                    </div>
                    <p className="text-xs text-purple-600 mb-3">
                      {formData.transType === 1
                        ? t('warehouse.modules.inventoryCorrection.serialBatch.selectSerialsToRemove', {}, 'Velg hvilke serienumre som skal tas ut fra lokasjonen')
                        : t('warehouse.modules.inventoryCorrection.serialBatch.registerSerialsToAdd', {}, 'Registrer nye serienumre som skal legges inn på lokasjonen')}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowSerialModal(true)}
                        disabled={isAdding}
                        className="flex-1 px-4 py-2.5 sm:py-2 rounded-lg font-medium flex items-center justify-center space-x-2 text-sm min-h-[44px] sm:min-h-0 bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                      >
                        <Hash className="h-4 w-4" />
                        <span>{formData.transType === 1 ? t('warehouse.modules.inventoryCorrection.serialBatch.selectSerials', {}, 'Velg serienumre') : t('warehouse.modules.inventoryCorrection.serialBatch.registerSerials', {}, 'Registrer serienumre')}</span>
                      </button>
                      <button
                        onClick={handleReset}
                        className="p-2.5 sm:p-2 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                        title={t('common.buttons.reset')}
                      >
                        <RotateCcw className="h-5 w-5 sm:h-4 sm:w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (currentItem.itemType === 4 || currentItem.itemType === 5) ? (
                /* Batch item (ItemType=4/5) → vis batch-knapp */
                <div className="space-y-2">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-4 w-4 text-orange-600" />
                      <span className="text-sm font-medium text-orange-800">{t('warehouse.modules.inventoryCorrection.serialBatch.batchControlledItem', {}, 'Batchstyrt vare')}</span>
                    </div>
                    <p className="text-xs text-orange-600 mb-3">
                      {formData.transType === 1
                        ? t('warehouse.modules.inventoryCorrection.serialBatch.selectBatchesToRemove', {}, 'Velg hvilke batcher som skal tas ut fra lokasjonen')
                        : t('warehouse.modules.inventoryCorrection.serialBatch.registerBatchesToAdd', {}, 'Registrer batcher som skal legges inn på lokasjonen')}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowBatchModal(true)}
                        disabled={isAdding}
                        className="flex-1 px-4 py-2.5 sm:py-2 rounded-lg font-medium flex items-center justify-center space-x-2 text-sm min-h-[44px] sm:min-h-0 bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                      >
                        <Package className="h-4 w-4" />
                        <span>{formData.transType === 1 ? t('warehouse.modules.inventoryCorrection.serialBatch.selectBatches', {}, 'Velg batcher') : t('warehouse.modules.inventoryCorrection.serialBatch.registerBatches', {}, 'Registrer batcher')}</span>
                      </button>
                      <button
                        onClick={handleReset}
                        className="p-2.5 sm:p-2 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                        title={t('common.buttons.reset')}
                      >
                        <RotateCcw className="h-5 w-5 sm:h-4 sm:w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Standard item → vis kvantitetsinput */
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      {t('warehouse.modules.inventoryCorrection.labels.quantityToCorrect')}
                    </label>
                    <NumericInput
                      ref={quantityInputRef}
                      mode="decimal"
                      quantity
                      value={formData.quantity}
                      onChange={(e) => setFormData((prev: InventoryCorrectionFormData) => ({ ...prev, quantity: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && currentItem && currentLocation && formData.quantity.trim()) {
                          handleAddCorrection();
                        }
                      }}
                      placeholder={t('warehouse.modules.inventoryCorrection.placeholders.enterQuantity')}
                      className="w-full p-2.5 sm:p-2 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm min-h-[44px] sm:min-h-0"
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={handleAddCorrection}
                      disabled={!currentItem || !currentLocation || !formData.quantity.trim() || isAdding}
                      className={`flex-1 px-4 py-2.5 sm:py-2 rounded font-medium flex items-center justify-center space-x-2 text-sm min-h-[44px] sm:min-h-0 ${
                        currentItem && currentLocation && formData.quantity.trim() && !isAdding
                          ? 'bg-red-500 text-white hover:bg-red-600'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {isAdding ? (
                        <Loader className="animate-spin h-4 w-4" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                      <span>{t('warehouse.modules.inventoryCorrection.actions.correct')}</span>
                    </button>

                    <button
                      onClick={handleReset}
                      className="p-2.5 sm:p-2 border border-gray-300 rounded hover:bg-gray-50 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                      title={t('common.buttons.reset')}
                    >
                      <RotateCcw className="h-5 w-5 sm:h-4 sm:w-4" />
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        </div>

        {/* Korreksjons liste */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-3 sm:p-4 border-b">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3">
              <h2 className="text-sm sm:text-base font-semibold flex items-center">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-green-500 flex-shrink-0" />
                <span>{t('warehouse.modules.inventoryCorrection.labels.corrections')} ({session.lines.length})</span>
                {filterText && (
                  <span className="ml-2 text-xs sm:text-sm text-gray-500">
                    - {t('warehouse.modules.inventoryCorrection.labels.showing')} {filteredLines.length}
                  </span>
                )}
              </h2>
            </div>

            {/* Filter input */}
            <div className="relative">
              <input
                type="text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder={t('warehouse.modules.inventoryCorrection.placeholders.filterItemOrLocation')}
                className="w-full pl-10 pr-10 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm min-h-[44px] sm:min-h-0"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              {filterText && (
                <button
                  onClick={() => setFilterText('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center -mr-2 sm:mr-0"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {session.lines.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-gray-500">
              <Package className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm sm:text-base">{t('warehouse.modules.inventoryCorrection.messages.noCorrectionsYet')}</p>
              <p className="text-xs sm:text-sm">{t('warehouse.modules.inventoryCorrection.messages.scanToStart')}</p>
            </div>
          ) : filteredLines.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-gray-500">
              <Search className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm sm:text-base">{t('warehouse.modules.inventoryCorrection.messages.noCorrectionsMatchedFilter')}</p>
              <p className="text-xs sm:text-sm">{t('warehouse.modules.inventoryCorrection.messages.tryDifferentSearchOrRemoveFilter')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {filteredLines.map((line: InventoryCorrectionLine) => (
                <div key={line.lineID} className="p-3 sm:p-4 hover:bg-gray-50">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                        <div className="font-mono text-xs sm:text-sm font-semibold break-all">
                          {line.itemNo}
                          {itemUnitMap[line.itemNo] && (
                            <span className="ml-2 font-sans font-normal text-gray-400">{itemUnitMap[line.itemNo]}</span>
                          )}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600 break-words">
                          {lineItemDescription(line)}
                        </div>
                        {lineComment(line) && (
                          <div className="text-xs sm:text-sm text-gray-500 italic break-words">
                            {t('warehouse.modules.inventoryCorrection.commentLine', { '0': lineComment(line) }, `Kommentar: ${lineComment(line)}`)}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs sm:text-sm text-gray-500">
                        <span className="flex items-center whitespace-nowrap">
                          <MapPin className="h-3 w-3 mr-1 text-blue-500 flex-shrink-0" />
                          <span className="break-all">{line.locationName}</span>
                        </span>

                        {/* Serial number badge */}
                        {line.itemSerialNo && (
                          <span className="flex items-center whitespace-nowrap">
                            <Hash className="h-3 w-3 mr-1 text-purple-500 flex-shrink-0" />
                            <span className="font-mono text-purple-700">{line.itemSerialNo}</span>
                          </span>
                        )}

                        {/* Batch badge */}
                        {line.itemBatchNo && (
                          <span className="flex items-center whitespace-nowrap">
                            <Package className="h-3 w-3 mr-1 text-orange-500 flex-shrink-0" />
                            <span className="font-mono text-orange-700">{line.itemBatchNo}</span>
                            {line.itemBatchExpDate && (
                              <span className="text-xs text-gray-500 ml-1">
                                ({new Date(line.itemBatchExpDate).toLocaleDateString('no-NO')})
                              </span>
                            )}
                          </span>
                        )}

                        <span className="flex items-center whitespace-nowrap">
                          {line.transType === 1 ? (
                            <Minus className="h-3 w-3 mr-1 text-red-500 flex-shrink-0" />
                          ) : (
                            <PlusCircle className="h-3 w-3 mr-1 text-green-500 flex-shrink-0" />
                          )}
                          <span className={line.transType === 1 ? 'text-red-600' : 'text-green-600'}>
                            {line.transTypeText}
                          </span>
                        </span>

                        <span className="break-words">
                          {t('warehouse.modules.inventoryCorrection.labels.reason')}: {inventoryCorrectionService.getReasonCodeDescription(line.reasonCode)}
                        </span>

                        {/* Quantity - editable or display */}
                        {editingLine && editingLine.lineID === line.lineID ? (
                          <div className="flex items-center gap-2 w-full sm:w-auto">
                            <span>{t('warehouse.general.quantityShort')}:</span>
                            <NumericInput
                              ref={editQuantityInputRef}
                              mode="decimal"
                              quantity
                              value={editingLine.quantity}
                              onChange={(e) => setEditingLine(prev => prev ? { ...prev, quantity: e.target.value } : null)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveEdit();
                                } else if (e.key === 'Escape') {
                                  handleCancelEdit();
                                }
                              }}
                              className="flex-1 sm:w-20 px-2 py-1.5 sm:py-1 border border-red-300 rounded text-sm focus:ring-1 focus:ring-red-500 focus:border-red-500 min-h-[44px] sm:min-h-0"
                            />
                            <div className="flex items-center gap-1">
                              <button
                                onClick={handleSaveEdit}
                                disabled={updatingLineId === line.lineID}
                                className="p-2 sm:p-1 text-green-600 hover:bg-green-100 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                                title={t('common.buttons.save')}
                              >
                                {updatingLineId === line.lineID ? (
                                  <Loader className="h-4 w-4 sm:h-3 sm:w-3 animate-spin" />
                                ) : (
                                  <Check className="h-4 w-4 sm:h-3 sm:w-3" />
                                )}
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="p-2 sm:p-1 text-gray-600 hover:bg-gray-100 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                                title={t('common.buttons.cancel')}
                              >
                                <X className="h-4 w-4 sm:h-3 sm:w-3" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className={`font-medium whitespace-nowrap ${
                            line.quantity > 0 ? 'text-green-600' : line.quantity < 0 ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {t('warehouse.general.quantityShort')}: {line.quantity > 0 ? '+' : ''}{line.quantity}
                          </span>
                        )}

                        <span className="whitespace-nowrap">{new Date(line.transDt).toLocaleTimeString('no-NO')}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs sm:text-sm whitespace-nowrap">
                        {line.statusText}
                      </span>

                      {/* Action buttons - kun vis hvis ikke i edit mode */}
                      {!editingLine && (
                        <div className="flex items-center gap-1">
                          {/* Edit button - disabled for serial/batch lines */}
                          {!line.itemSerialNo && !line.itemBatchNo && (
                            <button
                              onClick={() => handleStartEdit(line)}
                              className="p-2 sm:p-1 text-blue-600 hover:bg-blue-100 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                              title={t('warehouse.modules.inventoryCorrection.actions.editQuantity')}
                            >
                              <Edit2 className="h-4 w-4 sm:h-3 sm:w-3" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteLine(line)}
                            disabled={deletingLineId === line.lineID}
                            className="p-2 sm:p-1 text-red-600 hover:bg-red-100 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                            title={t('warehouse.modules.inventoryCorrection.actions.deleteCorrection')}
                          >
                            {deletingLineId === line.lineID ? (
                              <Loader className="h-4 w-4 sm:h-3 sm:w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 sm:h-3 sm:w-3" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modaler */}
      <LocationSelectionModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onSelect={handleLocationSelection}
        onScanLocation={handleScanLocationInModal}
        itemNo={currentItem?.itemNo || ''}
        itemDescription={currentItem?.description || ''}
        itemUnitTxt={currentItem?.itemUnitTxt}
        locations={availableLocations}
        loading={modalLoading}
      />

      <ItemSelectionModal
        isOpen={showItemModal}
        onClose={() => setShowItemModal(false)}
        onSelect={handleItemSelection}
        locationName={modalLocationName}
        warehouseID={modalWarehouseID}
        items={availableItems}
        loading={modalLoading}
      />

      <ItemSearchModal
        isOpen={showItemSearchModal}
        onClose={() => setShowItemSearchModal(false)}
        onSelect={handleItemSearchSelect}
        title={t('warehouse.modules.inventoryCorrection.modals.searchAndSelectItem')}
        warehouseId={warehouse || currentWarehouse}
        fetchLocations={async (itemNo: string) => {
          const locations = await inventoryCorrectionService.getLocationsForItem(itemNo, warehouse || currentWarehouse);
          return locations.map(loc => ({
            locationName: loc.locationName,
            warehouseID: loc.warehouseID || '',
            availableQuantity: loc.totalQuantity // Use totalQuantity for corrections (not just available)
          }));
        }}
      />

      <LocationSearchModal
        isOpen={showLocationSearchModal}
        onClose={() => setShowLocationSearchModal(false)}
        onSelect={handleLocationSearchSelect}
        title={t('warehouse.modules.inventoryCorrection.modals.searchLocation')}
      />

      {/* Serial Correction Modal */}
      {currentItem && currentLocation && (
        <SerialCorrectionModal
          isOpen={showSerialModal}
          onClose={() => setShowSerialModal(false)}
          onComplete={handleSerialCorrectionComplete}
          mode={formData.transType === 1 ? 'remove' : 'add'}
          itemNo={currentItem.itemNo}
          itemDescription={currentItem.description}
          binCode={currentLocation.locationName}
          warehouseId={currentLocation.warehouseID || warehouse}
        />
      )}

      {/* Batch Correction Modal */}
      {currentItem && currentLocation && (
        <BatchCorrectionModal
          isOpen={showBatchModal}
          onClose={() => setShowBatchModal(false)}
          onComplete={handleBatchCorrectionComplete}
          mode={formData.transType === 1 ? 'remove' : 'add'}
          itemNo={currentItem.itemNo}
          itemDescription={currentItem.description}
          binCode={currentLocation.locationName}
          warehouseId={currentLocation.warehouseID || warehouse}
          itemType={currentItem.itemType ?? 4}
        />
      )}

      {/* Extend Shelf Modal — utvid hylle under varetelling/korreksjon.
          Blank prefill: operatør scanner/skriver parent-BinCode.
          Ved opprettelse: auto-velg ny bin som gjeldende lokasjon. */}
      <ExtendShelfModal
        isOpen={showExtendShelfModal}
        onClose={() => setShowExtendShelfModal(false)}
        initialParentBinCode=""
        onCreated={(newBinCode) => {
          // Operatør har opprettet ny lokasjon → auto-velg som gjeldende lokasjon for korreksjon
          const inheritedWarehouseID = currentLocation?.warehouseID || warehouse;
          setCurrentLocation({
            locationName: newBinCode,
            warehouseID: inheritedWarehouseID,
          });
          setShowExtendShelfModal(false);
        }}
      />
    </div>
  );
};
